import { useTranslation } from 'react-i18next';
import { Info } from 'lucide-react';
import { FlagIcon } from '@/components/FlagIcon';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const COUNTRIES = [
  { code: 'SE', flag: 'sv' },
  { code: 'NO', flag: 'no' },
  { code: 'DK', flag: 'da' },
  { code: 'FI', flag: 'fi' },
] as const;

interface CountryFilterProps {
  selected: string[];
  onChange: (countries: string[]) => void;
  className?: string;
  showTooltip?: boolean;
}

export function CountryFilter({ selected, onChange, className, showTooltip = false }: CountryFilterProps) {
  const { t } = useTranslation();

  const toggle = (code: string) => {
    onChange(
      selected.includes(code)
        ? selected.filter((c) => c !== code)
        : [...selected, code]
    );
  };

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <span className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground mr-1">
        {t('filter.byCountry')}
        {showTooltip && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3.5 h-3.5 text-muted-foreground/70 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[220px] text-xs">
                {t('filter.countryTooltip')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </span>
      {COUNTRIES.map(({ code, flag }) => {
        const isActive = selected.includes(code);
        return (
          <button
            key={code}
            type="button"
            onClick={() => toggle(code)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors',
              isActive
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card border-border hover:bg-muted text-foreground'
            )}
          >
            <FlagIcon code={flag} className="w-5 h-3.5" />
            {t(`filter.countries.${code}`)}
          </button>
        );
      })}
    </div>
  );
}
